import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import CompanyPicker from 'components/CompanyPicker';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faClockRotateLeft,
  faFloppyDisk,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  getPriceListGetActiveFacilityExchangeVersionQueryKey,
  getPriceListGetAllVersionsQueryKey,
  usePriceListActivateVersion,
  usePriceListGetActiveFacilityExchangeVersion,
  usePriceListGetAllVersions,
  usePriceListGetFacilityExchangeVersion,
  usePriceListSaveFacilityExchangeVersion,
} from 'api/price-list/price-list';
import { PriceListGetAllVersionsType } from 'api/model';
import { showException } from 'utils/exception-helper';
import { formatDate, toDateOnlyString } from 'utils/date-helpers';
import ConfirmModal from 'components/Modal/ConfirmModal';
import useModalStack from 'contexts/modal/useModalStack';

// #region Types
interface FacilityExchangePriceListModel {
  id: number;
  activeFrom: Date;
  activeTo?: Date | null;
  comment?: string | null;
  createdByName: string;
  created: Date;
  prices: FacilityExchangePriceModel[];
}

interface FacilityExchangePriceModel {
  id: number;
  fromCompanyId: number;
  fromCompanyName: string;
  price: number;
}
// #endregion

// #region Styled Components
const Wrapper = styled.div`padding: 20px;`;
const TopBar = styled.div`display: flex; align-items: center; gap: 16px; margin-bottom: 20px; flex-wrap: wrap;`;
const SettingsRow = styled.div`display: flex; align-items: flex-end; gap: 16px; margin-bottom: 16px; flex-wrap: wrap;`;
const SearchRow = styled.div`margin-bottom: 12px;`;
const PriceInput = styled(Input)`width: 100%; padding: 2px 4px;`;
const CommentInput = styled(Input)`width: 300px;`;

const DeleteButton = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground.error};
  padding: 2px 6px;
  &:hover { opacity: 0.7; }
`;

const CompanyPickerCell = styled.div`
  width: 100%; min-width: 0;
  > div { display: block; width: 100%; }
  .searchable-select-fake-select {
    width: 100%; overflow: hidden;
    > div:first-child { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
`;

const EmptyState = styled.div`padding: 20px; text-align: center; color: ${({ theme }) => theme.colors.foreground.secondary};`;
const MyTable = styled(Table)`margin-bottom: 16px; .table-row > * { min-width: 0; }` as typeof Table;
const StyledButton = styled(Button)`padding: 8px 16px; white-space: nowrap;` as typeof Button;

const HistoryBanner = styled.div`
  background: ${({ theme }) => theme.colors.background.highlight ?? '#e8f4fd'};
  border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 4px;
  padding: 12px 16px; margin-bottom: 16px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
`;
const HistoryBannerButtons = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;

const HistoryPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 4px;
  padding: 12px; margin-bottom: 16px; max-height: 350px; max-width: 500px; overflow-y: auto;
`;

const HistoryItem = styled.div<{ $active?: boolean; $selected?: boolean }>`
  padding: 4px 8px; font-size: 13px;
  background: ${({ $active, $selected, theme }) =>
    $selected ? theme.colors.background.button : $active ? theme.colors.background.highlight : 'transparent'};
  color: ${({ $selected, theme }) => $selected ? theme.colors.foreground.button : 'inherit'};
  border-radius: 2px; cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const DuplicateWarning = styled.div`color: ${({ theme }) => theme.colors.foreground.error}; font-weight: 500; margin-bottom: 8px;`;
// #endregion

// #region Helpers
const toDateInputValue = toDateOnlyString;
const toDateStr = toDateOnlyString;
const todayStr = () => formatDate(new Date());
const isActiveNow = (activeFrom: Date | string, activeTo?: Date | string | null): boolean => {
  const today = todayStr();
  const from = toDateStr(activeFrom);
  if (from > today) return false;
  if (!activeTo) return true;
  return toDateStr(activeTo) >= today;
};
const isDeadVersion = (activeFrom: Date | string, activeTo?: Date | string | null): boolean => {
  if (!activeTo) return false;
  return toDateStr(activeFrom) > toDateStr(activeTo);
};
// #endregion

interface PriceFormItem { fromCompanyId: number; price: number; }
interface FormValues { comment: string; activeFrom: string; activeTo: string; prices: PriceFormItem[]; }

interface PriceRow {
  fieldIndex: number; fieldId: string;
  fromCompanyId: number; fromCompanyName: string; price: number;
}

interface RenderProps {
  setValue: ReturnType<typeof useForm<FormValues>>['setValue'];
  register: ReturnType<typeof useForm<FormValues>>['register'];
  remove: (index: number) => void;
  onCompanySelected: (fieldIndex: number, companyId: number, companyName: string) => void;
  isReadOnly: boolean;
}

const toFormValues = (version: FacilityExchangePriceListModel | undefined | null): FormValues => ({
  comment: version?.comment ?? '',
  activeFrom: toDateInputValue(version?.activeFrom) || todayStr(),
  activeTo: toDateInputValue(version?.activeTo),
  prices: version?.prices.map(p => ({ fromCompanyId: p.fromCompanyId, price: p.price })) ?? [],
});

interface Props {
  lookupVersionId?: number | null;
  onLookupHandled?: () => void;
  lookupElement?: React.ReactNode;
}

const FacilityExchangePriceListPage: FC<Props> = ({ lookupVersionId, onLookupHandled, lookupElement }) => {
  const queryClient = useQueryClient();
  const modalStack = useModalStack();

  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [companyNameCache, setCompanyNameCache] = useState<Record<number, string>>({});
  const [viewingVersionId, setViewingVersionId] = useState<number | null>(null);

  useEffect(() => {
    if (lookupVersionId != null && lookupVersionId > 0) {
      setViewingVersionId(lookupVersionId);
      setShowHistory(false);
      onLookupHandled?.();
    }
  }, [lookupVersionId, onLookupHandled]);

  // --- Queries ---
  const activeVersionQuery = usePriceListGetActiveFacilityExchangeVersion();
  const versionsQuery = usePriceListGetAllVersions({ type: PriceListGetAllVersionsType.NUMBER_5 });
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);
  const activeVersion = activeVersionQuery.data ?? null;

  const historicalVersionQuery = usePriceListGetFacilityExchangeVersion(
    { id: viewingVersionId! },
    { query: { enabled: viewingVersionId != null } }
  );

  const isViewingHistory = viewingVersionId != null;
  const viewedVersion = historicalVersionQuery.data ?? null;

  // --- Mutations ---
  const saveMutation = usePriceListSaveFacilityExchangeVersion();
  const activateVersionMutation = usePriceListActivateVersion();
  const isMutating = saveMutation.isLoading || activateVersionMutation.isLoading;

  // --- Form ---
  const { register, control, watch, reset, setValue, handleSubmit, formState } = useForm<FormValues>({ defaultValues: toFormValues(null) });
  const { fields, append, remove } = useFieldArray({ control, name: 'prices' });

  const watchedActiveFrom = watch('activeFrom');
  const watchedActiveTo = watch('activeTo');
  const watchedPrices = useWatch({ control, name: 'prices' });

  const [lastSyncedId, setLastSyncedId] = useState<number | null>(null);
  useEffect(() => {
    if (activeVersion && activeVersion.id !== lastSyncedId && !isViewingHistory) {
      reset(toFormValues(activeVersion));
      setLastSyncedId(activeVersion.id);
      const cache: Record<number, string> = {};
      for (const p of activeVersion.prices) cache[p.fromCompanyId] = p.fromCompanyName;
      setCompanyNameCache(prev => ({ ...prev, ...cache }));
    }
  }, [activeVersion, lastSyncedId, reset, isViewingHistory]);

  useEffect(() => {
    if (viewedVersion) {
      reset(toFormValues(viewedVersion));
      const cache: Record<number, string> = {};
      for (const p of viewedVersion.prices) cache[p.fromCompanyId] = p.fromCompanyName;
      setCompanyNameCache(prev => ({ ...prev, ...cache }));
    }
  }, [viewedVersion, reset]);

  // --- Duplicate detection ---
  const duplicateIndices = useMemo(() => {
    const indices = new Set<number>();
    if (!watchedPrices) return indices;
    const seen = new Map<number, number[]>();
    watchedPrices.forEach((p, i) => {
      if (!p.fromCompanyId) return;
      const existing = seen.get(p.fromCompanyId);
      if (existing) { existing.push(i); seen.set(p.fromCompanyId, existing); }
      else seen.set(p.fromCompanyId, [i]);
    });
    for (const group of seen.values()) {
      if (group.length > 1) for (const idx of group) indices.add(idx);
    }
    return indices;
  }, [watchedPrices]);

  const hasDuplicates = duplicateIndices.size > 0;
  const visibleVersions = useMemo(() => versions.filter(v => !isDeadVersion(v.activeFrom, v.activeTo)), [versions]);
  const { isDirty } = formState;

  const hasHistoricalOverlap = useMemo(() => {
    if (!isDirty || !watchedActiveFrom) return false;
    const today = todayStr();
    if (watchedActiveFrom >= today) return false;
    const newTo = watchedActiveTo || '9999-12-31';
    return visibleVersions.some(v => {
      const vFrom = toDateStr(v.activeFrom);
      if (vFrom >= today) return false;
      const vTo = toDateStr(v.activeTo) || '9999-12-31';
      return watchedActiveFrom <= vTo && vFrom <= newTo;
    });
  }, [visibleVersions, watchedActiveFrom, watchedActiveTo, isDirty]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (isDirty) e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const invalidateAll = async () => {
    await queryClient.invalidateQueries(getPriceListGetAllVersionsQueryKey());
    await queryClient.invalidateQueries(getPriceListGetActiveFacilityExchangeVersionQueryKey());
  };

  const handleReturnToActive = () => { setViewingVersionId(null); setLastSyncedId(null); };

  const handleViewVersion = (versionId: number) => {
    const ver = versions.find(v => v.id === versionId);
    if (ver && isActiveNow(ver.activeFrom, ver.activeTo)) handleReturnToActive();
    else setViewingVersionId(versionId);
  };

  const handleActivateVersion = () => {
    if (!viewedVersion) return;
    const label = `${toDateInputValue(viewedVersion.activeFrom)} → ${viewedVersion.activeTo ? toDateInputValue(viewedVersion.activeTo) : 'tills vidare'}`;
    modalStack.push(
      <ConfirmModal title="Aktivera version"
        content={`Är du säker på att du vill aktivera prislista ${label}? Den kommer aktiveras från och med nu.`}
        onConfirm={async () => {
          try {
            await activateVersionMutation.mutateAsync({ params: { id: viewedVersion.id } });
            await invalidateAll();
            setViewingVersionId(null); setLastSyncedId(null);
            toast.success('Prislistan har aktiverats');
          } catch (err) { showException(err); }
          modalStack.pop();
        }}
        onCancel={() => modalStack.pop()}
      />
    );
  };

  const doSave = async (data: FormValues) => {
    try {
      await saveMutation.mutateAsync({
        data: {
          comment: data.comment || undefined,
          activeFrom: new Date(data.activeFrom),
          activeTo: data.activeTo ? new Date(data.activeTo) : undefined,
          prices: data.prices.map(p => ({ fromCompanyId: p.fromCompanyId, price: p.price })),
        },
      });
      await invalidateAll();
      setLastSyncedId(null);
      toast.success('Prislistan har sparats');
    } catch (err) { showException(err); }
  };

  const onSave = handleSubmit(async (data) => {
    modalStack.push(
      <ConfirmModal title="Spara ny version"
        content={data.activeFrom <= todayStr()
          ? 'Den nya versionen kommer att aktiveras automatiskt.'
          : `Den nya versionen kommer att aktiveras automatiskt fr.o.m ${data.activeFrom}.`}
        onConfirm={async () => { modalStack.pop(); await doSave(data); }}
        onCancel={() => modalStack.pop()}
      />
    );
  });

  const handleCompanySelected = useCallback(
    (fieldIndex: number, companyId: number, companyName: string) => {
      setValue(`prices.${fieldIndex}.fromCompanyId`, companyId, { shouldDirty: true });
      setCompanyNameCache(prev => ({ ...prev, [companyId]: companyName }));
    },
    [setValue]
  );

  const sourceVersion = isViewingHistory ? viewedVersion : activeVersion;
  const rows: PriceRow[] = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return fields
      .map((field, i) => {
        const liveFromId = watchedPrices?.[i]?.fromCompanyId ?? field.fromCompanyId;
        const match = sourceVersion?.prices?.find(p => p.fromCompanyId === liveFromId);
        const fromName = match?.fromCompanyName ?? companyNameCache[liveFromId] ?? `#${liveFromId}`;
        const price = watchedPrices?.[i]?.price ?? 0;
        return { fieldIndex: i, fieldId: field.id, fromCompanyId: liveFromId, fromCompanyName: fromName, price };
      })
      .filter(row => !searchTerm || row.fromCompanyName.toLowerCase().includes(term));
  }, [fields, searchTerm, sourceVersion, watchedPrices, companyNameCache]);

  const rowClassName = useCallback(
    (row: PriceRow) => `row-${row.fieldIndex}${duplicateIndices.has(row.fieldIndex) ? ' duplicate-row' : ''}`,
    [duplicateIndices]
  );

  const columnSettings = useMemo((): ColumnSetting<PriceRow, RenderProps>[] => [
    { head: '#', width: 20, render: (row) => `${row.fieldIndex + 1}`, sortFunction: (a, b) => a.fieldIndex - b.fieldIndex },
    {
      head: 'Från anläggning', width: 250, focusable: true,
      render: (row, rp) => rp.isReadOnly ? row.fromCompanyName : (
        <CompanyPickerCell>
          <CompanyPicker selectedCompanyId={row.fromCompanyId}
            onCompanySelected={(company) => { if (company) rp.onCompanySelected(row.fieldIndex, company.companyID, company.name); }}
            notNullable />
        </CompanyPickerCell>
      ),
      sortFunction: (a, b) => a.fromCompanyName.localeCompare(b.fromCompanyName),
    },
    {
      head: 'Pris', width: 80, focusable: true,
      render: (row, rp) => (
        <PriceInput type="number" min={0} step={1} disabled={rp.isReadOnly}
          {...rp.register(`prices.${row.fieldIndex}.price`, { valueAsNumber: true })} />
      ),
      sortFunction: (a, b) => a.price - b.price,
    },
    {
      head: '', width: 30,
      render: (row, rp) => rp.isReadOnly ? null : (
        <DeleteButton type="button" onClick={() => rp.remove(row.fieldIndex)} title="Ta bort rad">
          <FontAwesomeIcon icon={faTrash} />
        </DeleteButton>
      ),
    },
  ], []);

  const renderProps: RenderProps = useMemo(
    () => ({ setValue, register, remove, onCompanySelected: handleCompanySelected, isReadOnly: isViewingHistory }),
    [setValue, register, remove, handleCompanySelected, isViewingHistory]
  );

  if (activeVersionQuery.isLoading) return <LoadingSpinner />;

  const canSave = isDirty && !hasDuplicates && !hasHistoricalOverlap && !isMutating && !isViewingHistory;

  return (
    <Wrapper>
      <style>{`.duplicate-row { background: #fff3cd !important; }`}</style>
      <TopBar>
        <StyledButton disabled={!canSave} onClick={canSave ? onSave : undefined}>
          <FontAwesomeIcon icon={faFloppyDisk} /> Spara
        </StyledButton>
        <StyledButton onClick={() => setShowHistory(v => !v)}>
          <FontAwesomeIcon icon={faClockRotateLeft} /> Historik
        </StyledButton>
        {lookupElement}
        {isMutating && <LoadingSpinner />}
      </TopBar>

      {showHistory && (
        <HistoryPanel>
          <strong>Versionshistorik</strong>
          {visibleVersions.length === 0 && <div>Inga versioner</div>}
          {visibleVersions.map(v => {
            const active = isActiveNow(v.activeFrom, v.activeTo);
            return (
              <HistoryItem key={v.id} $active={active} $selected={viewingVersionId === v.id} onClick={() => handleViewVersion(v.id)}>
                #{v.id} {new Date(v.activeFrom).toLocaleDateString('sv-SE')}
                {v.activeTo ? ` → ${new Date(v.activeTo).toLocaleDateString('sv-SE')}` : ' → tills vidare'}
                {v.comment ? ` — ${v.comment}` : ''}
                {active ? ' (Aktiv)' : ''}
              </HistoryItem>
            );
          })}
        </HistoryPanel>
      )}

      {isViewingHistory && viewedVersion && !isActiveNow(viewedVersion.activeFrom, viewedVersion.activeTo) && (
        <HistoryBanner>
          <span>
            {historicalVersionQuery.isLoading ? 'Laddar version...'
              : `Du visar version #${viewedVersion.id} — ${toDateInputValue(viewedVersion.activeFrom)} → ${viewedVersion.activeTo ? toDateInputValue(viewedVersion.activeTo) : 'tills vidare'}${viewedVersion.comment ? ` — ${viewedVersion.comment}` : ''}.`}
          </span>
          <HistoryBannerButtons>
            <StyledButton onClick={handleReturnToActive}>Tillbaka till aktiv version</StyledButton>
            <StyledButton onClick={handleActivateVersion}><FontAwesomeIcon icon={faBolt} /> Aktivera från nu</StyledButton>
          </HistoryBannerButtons>
        </HistoryBanner>
      )}

      <SettingsRow>
        <LabelWrap label="Aktiv från">
          <Input type="date" width={150} disabled={isViewingHistory} {...register('activeFrom')} />
        </LabelWrap>
        <LabelWrap label="Aktiv till">
          <Input type="date" width={150} disabled={isViewingHistory} {...register('activeTo')} />
        </LabelWrap>
        <LabelWrap label="Kommentar">
          <CommentInput type="text" disabled={isViewingHistory} {...register('comment')} />
        </LabelWrap>
      </SettingsRow>

      <SearchRow>
        <Input type="text" placeholder="Sök anläggning..." width={300} value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)} />
      </SearchRow>

      {hasDuplicates && <DuplicateWarning>Duplicerade anläggningar hittades. Varje anläggning får bara finnas en gång.</DuplicateWarning>}
      {hasHistoricalOverlap && <DuplicateWarning>Den valda perioden överlappar med en historisk prislista. Ändra fråndatum.</DuplicateWarning>}

      {rows.length === 0 ? (
        <EmptyState>{fields.length === 0 ? 'Inga priser. Klicka "Lägg till" för att börja.' : 'Inga rader matchar sökningen.'}</EmptyState>
      ) : (
        <MyTable fluid columnSettings={columnSettings} renderProps={renderProps} rows={rows} useColumnWidthAsFlexAmount rowClassName={rowClassName} />
      )}

      {!isViewingHistory && (
        <StyledButton type="button" onClick={() => append({ fromCompanyId: 0, price: 0 })}>
          <FontAwesomeIcon icon={faPlus} /> Lägg till
        </StyledButton>
      )}
    </Wrapper>
  );
};

export default FacilityExchangePriceListPage;
